import React, { useState, useMemo } from "react";
import { Card, CardBody, Col, Row, Input, Label, Spinner, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import { DataTable, Column, TableAction } from "../../../Components/Common/DataTable";
import { showModal } from "../../../hooks/useModal";
import { useLayoutStore } from "../../../stores/useLayoutStore";
import ImageLightbox from "../../../Components/Common/ImageLightbox";
import { useAlertesDocQuery } from "../../../hooks/queries/useRoadAlertQueries";
import type { AlerteDetail, AlerteDegradationDto } from "./types";

interface AlerteDetailContentProps {
  alerteId: number;
  alerte?: AlerteDetail | null;
  onClose?: () => void;
}

const AlerteDetailContent: React.FC<AlerteDetailContentProps> = ({ alerteId, alerte: initialAlerte }) => {
  const layoutModeType = useLayoutStore((state) => state.layoutModeType);
  const isDarkMode = layoutModeType === 'dark';

  const [activeTab, setActiveTab] = useState("1");

  const alerte = initialAlerte ?? null;
  const degradations: AlerteDegradationDto[] = useMemo(() => {
    const dtos = initialAlerte?.alertesDegradationDtos;
    return Array.isArray(dtos) && dtos.length > 0 ? (dtos as AlerteDegradationDto[]) : [];
  }, [initialAlerte]);

  const documentsQuery = useAlertesDocQuery(initialAlerte ? alerteId : null);
  const documents = (documentsQuery.data ?? []) as any[];
  const loading = documentsQuery.isLoading;

  // Colonnes pour le tableau des dégradations (chaque row est un AlerteDegradationDto)
  const degradationsColumns: Column<AlerteDegradationDto>[] = useMemo(() => [
    {
      key: "libelleDegradation",
      label: "Dégradation",
      sortable: true,
      visible: true,
      render: (_, row) => (
        <span style={{ color: isDarkMode ? '#fff' : '#212529' }}>
          {row?.degradation?.libelleDegradation ?? "-"}
        </span>
      ),
    },
    {
      key: "ensDegradation",
      label: "Ensemble Dégradation",
      sortable: true,
      visible: true,
      render: (_, row) => (
        <span style={{ color: isDarkMode ? '#adb5bd' : '#495057' }}>
          {row?.ensDegradation?.libelleEnsDegradation ?? "-"}
        </span>
      ),
    },
    {
      key: "niveauGravite",
      label: "Niveau de Gravité",
      sortable: true,
      visible: true,
      render: (_, row) => {
        const g = row?.niveauGravite;
        return (
          <span style={{ color: isDarkMode ? '#adb5bd' : '#495057' }}>
            {(g?.libelleNiveauGravite || g?.notation) ?? "-"}
          </span>
        );
      },
    },
  ], [isDarkMode]);

  // Actions pour chaque ligne du tableau dégradations (AlerteDegradationDto)
  const degradationsActions: TableAction<AlerteDegradationDto>[] = useMemo(() => [
    {
      label: "Voir les détails",
      icon: "ri-eye-line",
      onClick: (row) => {
        const d = row?.degradation;
        const e = row?.ensDegradation;
        const g = row?.niveauGravite;
        showModal({
          title: `Détails - ${d?.libelleDegradation ?? "Dégradation"}`,
          size: "lg",
          content: (
            <div className="p-2">
              <Row className="g-3">
                <Col md={6}>
                  <Label className="text-muted small">Dégradation</Label>
                  <Input type="text" value={d?.libelleDegradation ?? ""} readOnly />
                </Col>
                <Col md={6}>
                  <Label className="text-muted small">ID dégradation</Label>
                  <Input type="text" value={d?.idDegradation ?? ""} readOnly />
                </Col>
                <Col md={6}>
                  <Label className="text-muted small">Ensemble dégradation</Label>
                  <Input type="text" value={e?.libelleEnsDegradation ?? ""} readOnly />
                </Col>
                <Col md={6}>
                  <Label className="text-muted small">ID ensemble</Label>
                  <Input type="text" value={e?.idEnsDegradation ?? ""} readOnly />
                </Col>
                <Col md={6}>
                  <Label className="text-muted small">Niveau de gravité</Label>
                  <Input type="text" value={g?.libelleNiveauGravite ?? ""} readOnly />
                </Col>
                <Col md={6}>
                  <Label className="text-muted small">Notation</Label>
                  <Input type="text" value={g?.notation ?? ""} readOnly />
                </Col>
                <Col md={6}>
                  <Label className="text-muted small">ID alerte-dégradation</Label>
                  <Input type="text" value={row?.idAlerteDegradation ?? ""} readOnly />
                </Col>
              </Row>
            </div>
          ),
          closeOnBackdropClick: true,
          haveDefaultActionsButton: false,
        });
      },
      variant: "primary",
    },
  ], []);

  const lat = alerte?.latitude != null ? Number(alerte.latitude) : null;
  const lon = alerte?.longitude != null ? Number(alerte.longitude) : null;
  const hasMap = lat != null && lon != null && !Number.isNaN(lat) && !Number.isNaN(lon);
  // Bouton en haut à droite : ouvrir Google Maps
  const mapLinkUrl = hasMap ? `https://www.google.com/maps?q=${lat},${lon}` : null;
  // Onglet Carte : OpenStreetMap en iframe
  const mapEmbedUrl = hasMap
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lon - 0.01},${lat - 0.01},${lon + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat}%2C${lon}`
    : null;

  // Helper pour afficher une ligne info (sans bordure) avec support du thème
  const InfoRow = ({ label, value, icon, isLast = false }: { label: string; value: string | number | null | undefined; icon?: string; isLast?: boolean }) => (
    <div className={`d-flex align-items-start ${isLast ? 'mb-0' : 'mb-3'}`}>
      <div className="flex-shrink-0 me-3 mt-1">
        {icon && <i className={icon} style={{ fontSize: '1.1rem', color: isDarkMode ? '#86b7fe' : '#0d6efd' }}></i>}
      </div>
      <div className="flex-grow-1 min-w-0">
        <small 
          className="d-block mb-1" 
          style={{ 
            fontSize: '0.75rem', 
            fontWeight: 500,
            color: isDarkMode ? '#adb5bd' : '#6c757d'
          }}
        >
          {label}
        </small>
        <div 
          className="fw-semibold" 
          style={{ 
            fontSize: '0.95rem',
            color: isDarkMode ? '#fff' : '#212529'
          }}
        >
          {value ?? "-"}
        </div>
      </div>
    </div>
  );

  // Couleurs selon les IDs API : 3=TRES URGENT, 2=URGENT, 1=NORMAL, 0=NON DEFINI
  const getPrioriteBadgeClass = (priorite?: { libellePriorite?: string; id?: string; idPriorite?: number }) => {
    const rawId = priorite?.idPriorite ?? priorite?.id;
    const id = rawId != null ? Number(rawId) : null;
    if (id !== null && !Number.isNaN(id)) {
      if (id === 3) return "bg-danger text-white";           // TRES URGENT
      if (id === 2) return "bg-warning text-dark";          // URGENT
      if (id === 1) return "bg-info text-white";            // NORMAL
      if (id === 0) return "bg-secondary text-white";      // NON DEFINI
    }
    const l = String(priorite?.libellePriorite || "").toUpperCase();
    if (l.includes("TRES URGENT")) return "bg-danger text-white";
    if (l.includes("URGENT")) return "bg-warning text-dark";
    if (l.includes("NORMAL")) return "bg-info text-white";
    if (l.includes("NON DEFINI")) return "bg-secondary text-white";
    return "bg-secondary text-white";
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2 mb-0" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>Chargement des détails...</p>
      </div>
    );
  }

  return (
    <div className="alerte-detail-content" style={{ padding: '1rem 0' }}>
      {/* En-tête simplifié avec badges */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
        <div className="d-flex align-items-center gap-2 flex-grow-1">
          <span className={`badge ${getPrioriteBadgeClass(alerte?.priorite)} px-3 py-2`} style={{ fontSize: '0.85rem' }}>
            {alerte?.priorite?.libellePriorite || alerte?.libellePriorite || "—"}
          </span>
          <span className="badge bg-secondary px-3 py-2" style={{ fontSize: '0.85rem' }}>
            {alerte?.statutAlerte?.libelleStatutAlerte || alerte?.libelleStatutAlerte || "—"}
          </span>
        </div>
        {mapLinkUrl && (
          <a
            href={mapLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm text-white d-flex align-items-center gap-1"
            style={{ backgroundColor: "#311B91", borderColor: "#311B91" }}
          >
            <i className="ri-map-pin-line"></i> Carte (Google Maps)
          </a>
        )}
      </div>

      <Row className="g-4">
        {/* Colonne gauche : Identification & Description */}
        <Col lg={6}>
          <Card className="card-ageroute rounded-3 shadow-sm border-0 mb-4">
            <CardBody className="p-4">
              <h6 className="text-primary fw-bold mb-3 d-flex align-items-center">
                <i className="ri-file-text-line me-2"></i>Identification
              </h6>
              <div className="rounded p-3 mb-3" style={{ backgroundColor: isDarkMode ? '#2a2e32' : '#fff' }}>
                <InfoRow label="Type alerte" value={alerte?.typeAlerte?.libTypeAlerte || alerte?.libTypeAlerte} icon="ri-tag-line" />
                <InfoRow label="Type travaux" value={alerte?.typeTravaux?.libelleTypeTravaux || alerte?.libelleTypeTravaux} icon="ri-roadster-line" />
                <InfoRow label="Type chaussée" value={alerte?.typeChausse?.libelleTypeChausse || alerte?.libelleTypeChausse} icon="ri-road-map-line" />
                <InfoRow label="Nombre de dégradations" value={alerte?.nbreDegradation ?? alerte?.alertesDegradationDtos?.length} icon="ri-bar-chart-box-line" isLast />
              </div>
              {alerte?.description && (
                <div className="rounded p-3 mt-3" style={{ backgroundColor: isDarkMode ? '#2a2e32' : '#fff' }}>
                  <small className="d-block mb-2" style={{ fontSize: '0.75rem', fontWeight: 500, color: isDarkMode ? '#adb5bd' : '#6c757d' }}>Description</small>
                  <p className="mb-0" style={{ fontSize: '0.9rem', lineHeight: '1.6', color: isDarkMode ? '#fff' : '#212529' }}>{alerte.description}</p>
                </div>
              )}
            </CardBody>
          </Card>

          <Card className="card-ageroute rounded-3 shadow-sm border-0 mb-4">
            <CardBody className="p-4">
              <h6 className="text-primary fw-bold mb-3 d-flex align-items-center">
                <i className="ri-ruler-line me-2"></i>Dimensions
              </h6>
              <div className="rounded p-3" style={{ backgroundColor: isDarkMode ? '#2a2e32' : '#fff' }}>
                <Row className="g-3">
                  <Col xs={4}>
                    <div className="text-center">
                      <div className="text-primary fw-bold" style={{ fontSize: '1.5rem' }}>
                        {alerte?.surface ?? "-"}
                      </div>
                      <small className="d-block mt-1" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>m²</small>
                      <small className="d-block mt-1" style={{ fontSize: '0.7rem', color: isDarkMode ? '#adb5bd' : '#6c757d' }}>Surface</small>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="text-center">
                      <div className="text-primary fw-bold" style={{ fontSize: '1.5rem' }}>
                        {alerte?.longueur ?? "-"}
                      </div>
                      <small className="d-block mt-1" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>m</small>
                      <small className="d-block mt-1" style={{ fontSize: '0.7rem', color: isDarkMode ? '#adb5bd' : '#6c757d' }}>Longueur</small>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="text-center">
                      <div className="text-primary fw-bold" style={{ fontSize: '1.5rem' }}>
                        {alerte?.largeur ?? "-"}
                      </div>
                      <small className="d-block mt-1" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>m</small>
                      <small className="d-block mt-1" style={{ fontSize: '0.7rem', color: isDarkMode ? '#adb5bd' : '#6c757d' }}>Largeur</small>
                    </div>
                  </Col>
                </Row>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Colonne droite : Localisation & Métadonnées */}
        <Col lg={6}>
          <Card className="card-ageroute rounded-3 shadow-sm border-0 mb-4">
            <CardBody className="p-4">
              <h6 className="text-primary fw-bold mb-3 d-flex align-items-center">
                <i className="ri-map-pin-line me-2"></i>Localisation
              </h6>
              <div className="rounded p-3" style={{ backgroundColor: isDarkMode ? '#2a2e32' : '#fff' }}>
                <InfoRow label="Itinéraire" value={alerte?.itineraire} icon="ri-route-line" />
                <InfoRow label="Nom de voie" value={alerte?.nomVoie} icon="ri-roadster-line" />
                <InfoRow label="Quartier" value={alerte?.quartier} icon="ri-building-line" />
                <InfoRow label="Commune" value={alerte?.communes?.libelleCommune ?? alerte?.commune?.libelleCommune ?? alerte?.libelleCommune} icon="ri-map-pin-user-line" isLast />
              </div>
            </CardBody>
          </Card>

          <Card className="card-ageroute rounded-3 shadow-sm border-0 mb-4">
            <CardBody className="p-4">
              <h6 className="text-primary fw-bold mb-3 d-flex align-items-center">
                <i className="ri-time-line me-2"></i>Métadonnées
              </h6>
              <div className="rounded p-3" style={{ backgroundColor: isDarkMode ? '#2a2e32' : '#fff' }}>
                <InfoRow label="Date de création" value={alerte?.dateCreation} icon="ri-calendar-line" />
                <InfoRow label="Créé par" value={alerte?.creePar} icon="ri-user-line" isLast />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Onglets Dégradations / Photos / Carte */}
      <Card className="card-ageroute rounded-3 shadow-sm border-0 mt-2 mb-0">
        <CardBody className="p-0">
          <Nav tabs className="modern-tabs border-0 px-3 pt-3 mb-0">
            <NavItem>
              <NavLink
                className={classnames("py-3 px-4 border-0", { active: activeTab === "1" })}
                onClick={() => setActiveTab("1")}
                style={{ cursor: "pointer" }}
              >
                <i className="ri-list-check-2 me-2"></i>
                Dégradations
                {degradations.length > 0 && (
                  <span className={`badge ms-2 ${activeTab === "1" ? "bg-primary" : "bg-secondary"}`}>{degradations.length}</span>
                )}
              </NavLink>
            </NavItem>
<NavItem>
              <NavLink
                className={classnames("py-3 px-4 border-0", { active: activeTab === "2" })}
                onClick={() => setActiveTab("2")}
                style={{ cursor: "pointer" }}
              >
                <i className="ri-image-line me-2"></i>
                Photos
                {documents.length > 0 && (
                  <span className={`badge ms-2 ${activeTab === "2" ? "bg-primary" : "bg-secondary"}`}>{documents.length}</span>
                )}
              </NavLink>
            </NavItem>
<NavItem>
              <NavLink
                className={classnames("py-3 px-4 border-0", { active: activeTab === "3" })}
                onClick={() => setActiveTab("3")}
                style={{ cursor: "pointer" }}
              >
                <i className="ri-map-2-line me-2"></i>
                Carte
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={activeTab} className="p-4">
            <TabPane tabId="1">
              <DataTable
                instanceId={`degradations-modal-${alerteId}`}
                columns={degradationsColumns}
                data={degradations}
                isLoading={false}
                exportExcel={false}
                exportPDF={false}
                actions={degradationsActions}
                actionsColumnLabel="Actions"
                emptyState={
                  <div className="text-center py-5">
                    <i className="ri-file-list-line" style={{ fontSize: "3rem", color: isDarkMode ? '#6c757d' : '#6c757d' }}></i>
                    <p className="mt-2 mb-0" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>Aucune dégradation enregistrée pour cette alerte.</p>
                  </div>
                }
                pageSize={10}
                searchable={true}
                pagination={degradations.length > 10}
              />
            </TabPane>
            <TabPane tabId="2">
              {documents.length > 0 ? (
                <ImageLightbox
                  images={documents
                    .filter((d: any) => d.url || d.fileBase64)
                    .map((d: any, idx: number) => ({
                      src: d.url || (d.fileBase64 ? `data:image/jpeg;base64,${d.fileBase64}` : ""),
                      alt: `Document ${idx + 1}`,
                      title: `Photo ${idx + 1}`,
                    }))}
                >
                  {(openAt) => (
                    <Row className="g-3">
                      {documents.map((d: any, idx: number) => {
                        const hasImage = d.url || d.fileBase64;
                        const src = d.url || (d.fileBase64 ? `data:image/jpeg;base64,${d.fileBase64}` : "");
                        const imageIndex = documents.slice(0, idx).filter((x: any) => x.url || x.fileBase64).length;
                        return (
                          <Col key={d.id ?? idx} sm={6} md={4} lg={3}>
                            <div
                              className="rounded overflow-hidden border"
                              style={{
                                backgroundColor: isDarkMode ? "#2a2e32" : "#f8f9fa",
                                cursor: hasImage ? "pointer" : "default",
                              }}
                              onClick={() => hasImage && openAt(imageIndex)}
                              role={hasImage ? "button" : undefined}
                              tabIndex={hasImage ? 0 : undefined}
                              onKeyDown={(e) => hasImage && (e.key === "Enter" || e.key === " ") && openAt(imageIndex)}
                            >
                              {hasImage ? (
                                <img
                                  src={src}
                                  alt={`Document ${idx + 1}`}
                                  className="img-fluid w-100"
                                  style={{ objectFit: "cover", maxHeight: 180, display: "block" }}
                                />
                              ) : (
                                <div className="text-center p-4" style={{ backgroundColor: isDarkMode ? "#2a2e32" : "#f8f9fa" }}>
                                  <i className="ri-image-line fs-1" style={{ color: isDarkMode ? "#6c757d" : "#6c757d" }} />
                                </div>
                              )}
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </ImageLightbox>
              ) : (
                <div className="text-center py-5 rounded" style={{ backgroundColor: isDarkMode ? "#2a2e32" : "#f8f9fa" }}>
                  <i className="ri-image-line" style={{ fontSize: "3rem", color: isDarkMode ? "#6c757d" : "#6c757d" }} />
                  <p className="mt-2 mb-0" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Aucune photo disponible.</p>
                </div>
              )}
            </TabPane>
            <TabPane tabId="3">
              {mapEmbedUrl ? (
                <div className="rounded overflow-hidden border">
                  <iframe
                    src={mapEmbedUrl}
                    title="Localisation (OpenStreetMap)"
                    width="100%"
                    height="380"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                </div>
              ) : (
                <div className="text-center py-5 rounded" style={{ backgroundColor: isDarkMode ? '#2a2e32' : '#f8f9fa' }}>
                  <i className="ri-map-pin-line" style={{ fontSize: "3rem", color: isDarkMode ? '#6c757d' : '#6c757d' }}></i>
                  <p className="mt-2 mb-0" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>Aucune localisation disponible.</p>
                </div>
              )}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
};

export default AlerteDetailContent;
